import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
    User as FirebaseUser,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, UserRole } from '../types';

interface AuthContextType {
    user: FirebaseUser | null;
    userProfile: User | null;
    isLoading: boolean;
    isAdmin: boolean;
    isApproved: boolean;
    error: string | null;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, displayName: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const googleProvider = new GoogleAuthProvider();

// Admin emails that get admin role automatically
const ADMIN_EMAILS = ['baglin.yann@gmail.com'];

// Dev bypass mode - for automated testing
const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

// Mock user for dev bypass mode
const DEV_MOCK_USER: User = {
    uid: 'dev-bypass-user',
    email: 'dev@test.com',
    displayName: 'Dev Test User',
    role: 'admin',
    isApproved: true,
    createdAt: new Date() as unknown as User['createdAt'],
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const [userProfile, setUserProfile] = useState<User | null>(DEV_BYPASS_AUTH ? DEV_MOCK_USER : null);
    const [isLoading, setIsLoading] = useState(!DEV_BYPASS_AUTH);
    const [error, setError] = useState<string | null>(null);

    // Helper to create or get user profile
    const ensureUserProfile = async (firebaseUser: FirebaseUser): Promise<User | null> => {
        try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            let userDoc = await getDoc(userDocRef);

            // If doc doesn't exist, wait briefly and retry (in case signUp is still writing)
            if (!userDoc.exists()) {
                await new Promise(resolve => setTimeout(resolve, 500));
                userDoc = await getDoc(userDocRef);
            }

            if (userDoc.exists()) {
                const data = userDoc.data();
                // Handle backwards compatibility: if isApproved is not set, 
                // check if this is an admin or default to true for existing users
                const isAdminEmail = ADMIN_EMAILS.includes(data.email || '');
                const isApproved = data.isApproved !== undefined
                    ? data.isApproved
                    : (isAdminEmail ? true : true); // Existing users without field are grandfathered in
                return { ...data, isApproved } as User;
            }

            // Create new profile for OAuth users
            const email = firebaseUser.email || '';
            const isAdminEmail = ADMIN_EMAILS.includes(email);
            const role: UserRole = isAdminEmail ? 'admin' : 'user';

            const newUser = {
                uid: firebaseUser.uid,
                email,
                displayName: firebaseUser.displayName || email.split('@')[0],
                role,
                isApproved: isAdminEmail, // Admins are auto-approved, others need approval
                createdAt: serverTimestamp(),
            };

            await setDoc(userDocRef, newUser);
            return { ...newUser, createdAt: new Date() } as unknown as User;
        } catch (err) {
            console.error('Error ensuring user profile:', err);
            // Return a basic profile so the app doesn't break
            const email = firebaseUser.email || '';
            const isAdminEmail = ADMIN_EMAILS.includes(email);
            return {
                uid: firebaseUser.uid,
                email,
                displayName: firebaseUser.displayName || email.split('@')[0],
                role: isAdminEmail ? 'admin' : 'user',
                isApproved: isAdminEmail,
                createdAt: new Date(),
            } as unknown as User;
        }
    };

    useEffect(() => {
        // Skip Firebase auth in dev bypass mode
        if (DEV_BYPASS_AUTH) {
            console.log('[AuthContext] Dev bypass mode enabled - skipping Firebase auth');
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log('Auth state changed:', firebaseUser?.email || 'logged out');
            setUser(firebaseUser);
            setError(null);

            if (firebaseUser) {
                try {
                    const profile = await ensureUserProfile(firebaseUser);
                    setUserProfile(profile);
                } catch (err) {
                    console.error('Error in auth state change:', err);
                    setError('Erreur de chargement du profil');
                }
            } else {
                setUserProfile(null);
            }

            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signIn = async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
    };

    const signUp = async (email: string, password: string, displayName: string) => {
        const credential = await createUserWithEmailAndPassword(auth, email, password);

        const isAdminEmail = ADMIN_EMAILS.includes(email);
        const role: UserRole = isAdminEmail ? 'admin' : 'user';

        const newUser = {
            uid: credential.user.uid,
            email: credential.user.email || email,
            displayName,
            role,
            isApproved: isAdminEmail, // Admins are auto-approved
            createdAt: serverTimestamp(),
        };

        await setDoc(doc(db, 'users', credential.user.uid), newUser);

        // Immediately set the user profile to avoid black screen
        setUserProfile({
            ...newUser,
            createdAt: new Date(),
        } as unknown as User);
    };

    const signInWithGoogle = async () => {
        await signInWithPopup(auth, googleProvider);
        // Profile will be created automatically in onAuthStateChanged
    };

    const signOut = async () => {
        await firebaseSignOut(auth);
        setUserProfile(null);
    };

    const isAdmin = userProfile?.role === 'admin';
    const isApproved = userProfile?.isApproved ?? false;

    return (
        <AuthContext.Provider
            value={{
                user,
                userProfile,
                isLoading,
                isAdmin,
                isApproved,
                error,
                signIn,
                signUp,
                signInWithGoogle,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
